import { useState } from 'react';
import { XMarkIcon, TrophyIcon } from '@heroicons/react/24/outline';
import StudentSearchCombobox from '@/components/shared/StudentSearchCombobox';
import { createNomination } from '../services/campaignService';

export default function CampaignNominationModal({
  campaign,
  schoolId,
  user,
  onClose,
  onSubmitted,
}) {
  const [student, setStudent] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    if (!student || submitting) return;

    setSubmitting(true);
    setError('');
    try {
      await createNomination(schoolId, campaign, student, reason, user);
      onSubmitted?.(student);
    } catch (submissionError) {
      setError(submissionError.message || 'Could not submit this nomination');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="campaign-action-title">
      <div className="modal-box max-w-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              {campaign.competition?.label || 'Competition'}
            </p>
            <h2 id="campaign-action-title" className="mt-1 text-xl font-bold">
              {campaign.banner?.actionLabel || 'Nominate a learner'}
            </h2>
            {campaign.competition?.instructions && (
              <p className="mt-2 text-sm text-base-content/65">
                {campaign.competition.instructions}
              </p>
            )}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-circle"
            onClick={onClose}
            aria-label="Close nomination"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form className="mt-6 space-y-5" onSubmit={submit}>
          <div>
            <label className="mb-2 block text-sm font-medium">Learner</label>
            {student ? (
              <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-3">
                <div>
                  <div className="font-medium">
                    {student.firstName} {student.surname || student.lastName}
                  </div>
                  {student.className && (
                    <div className="text-xs text-base-content/55">{student.className}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => setStudent(null)}
                >
                  Change
                </button>
              </div>
            ) : (
              <StudentSearchCombobox
                onSelect={setStudent}
                placeholder="Search for the learner to nominate"
                autoFocus
              />
            )}
          </div>

          <div>
            <label htmlFor="campaign-nomination-reason" className="mb-2 block text-sm font-medium">
              Why are you nominating them? <span className="font-normal text-base-content/45">(optional)</span>
            </label>
            <textarea
              id="campaign-nomination-reason"
              className="textarea textarea-bordered w-full"
              rows={3}
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="Add a short reason for the judges"
            />
          </div>

          <div className="rounded-xl bg-base-200/70 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium">
              <TrophyIcon className="h-4 w-4 text-warning" />
              Winner recognition
            </div>
            <p className="mt-1 text-base-content/65">
              {campaign.recognition?.valueName}
            </p>
          </div>

          {error && (
            <div className="alert alert-error text-sm" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="modal-action">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!student || submitting}
            >
              {submitting && <span className="loading loading-spinner loading-sm" />}
              Submit nomination
            </button>
          </div>
        </form>
      </div>
      <button className="modal-backdrop" onClick={onClose} aria-label="Close nomination">
        close
      </button>
    </div>
  );
}
